/* src/components/admin/CreateZipcodeDispatchZoneModal.tsx */
import React, { useEffect, useState } from "react";
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import { adminZipcodeDispatchZoneService, CreateZipcodeDispatchZoneData } from "../../services/admin.zipcode-dispatch-zone.service";
import { countryService, CountryFull } from "../../services/country.service";
import { errorService } from "../../services/error.service";
import { utilsService } from "../../services/utils.service";
import useForm, { ValidationRules } from "../../hooks/useForm";
import Swal from "sweetalert2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faSave,
    faGlobe,
    faMapPin,
    faToggleOn
} from "@fortawesome/free-solid-svg-icons";
import styles from "../../styles/components/CreateZipcodeDispatchZoneModal.module.css";
import modalStyles from "../../styles/common/Modal.module.css";

interface CreateZipcodeDispatchZoneModalProps {
    show: boolean;
    onHide: () => void;
    onSuccess: () => void;
}

interface CreateZipcodeDispatchZoneFormState {
    iso2Country: string;
    zipcode: string;
    status: number;
}

const CreateZipcodeDispatchZoneModal: React.FC<CreateZipcodeDispatchZoneModalProps> = ({
    show,
    onHide,
    onSuccess,
}) => {
    const [countries, setCountries] = useState<CountryFull[]>([]);
    const [loadingCountries, setLoadingCountries] = useState(false);
    const [loading, setLoading] = useState(false);
    const [locationInfo, setLocationInfo] = useState<{ city?: string; state?: string } | null>(null);
    const [zipcodeError, setZipcodeError] = useState<string | null>(null);
    const [validatingZipcode, setValidatingZipcode] = useState(false);

    const initialValues = React.useMemo<CreateZipcodeDispatchZoneFormState>(() => ({
        iso2Country: "",
        zipcode: "",
        status: 1,
    }), []);

    const validationRules = React.useMemo<ValidationRules<CreateZipcodeDispatchZoneFormState>>(() => ({
        iso2Country: (val) => (!val ? "El país es obligatorio" : null),
        zipcode: (val) => (!val?.trim() ? "El código postal es obligatorio" : null),
    }), []);

    const {
        values,
        errors,
        handleChange,
        handleSubmit,
        resetForm,
        setValues,
    } = useForm<CreateZipcodeDispatchZoneFormState>(
        initialValues,
        validationRules
    );

    const validateZipcode = async (zip: string, country: string) => {
        if (!zip.trim() || !country) {
            setLocationInfo(null);
            setZipcodeError(null);
            return false;
        }
        try {
            setValidatingZipcode(true);
            setZipcodeError(null);
            const loc = await utilsService.getLocationByZipcode({
                iso2code: country.toLowerCase(),
                zipcode: zip.trim()
            });
            if (loc) {
                setLocationInfo({ city: loc.city, state: loc.state });
                setZipcodeError(null);
                return true;
            } else {
                setLocationInfo(null);
                setZipcodeError(`Código postal inválido para ${country.toUpperCase()}.`);
                return false;
            }
        } catch (err) {
            console.error("Error validating zipcode:", err);
            setLocationInfo(null);
            setZipcodeError("Error al validar código postal.");
            return false;
        } finally {
            setValidatingZipcode(false);
        }
    };

    // Fetch countries list on mount
    useEffect(() => {
        const fetchCountries = async () => {
            try {
                setLoadingCountries(true);
                const data = await countryService.getAllCountries();
                // Filter countries that are active or send-enabled (or just show all senders)
                const senders = data.filter((c) => c.send === true);
                setCountries(senders.length > 0 ? senders : data);
                if (senders.length > 0) {
                    setValues((prev) => ({ ...prev, iso2Country: senders[0].iso2Country }));
                } else if (data.length > 0) {
                    setValues((prev) => ({ ...prev, iso2Country: data[0].iso2Country }));
                }
            } catch (error) {
                console.error("Error loading countries:", error);
            } finally {
                setLoadingCountries(false);
            }
        };
        fetchCountries();
    }, [setValues]);

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            if (values.zipcode && values.iso2Country) {
                validateZipcode(values.zipcode, values.iso2Country);
            } else {
                setLocationInfo(null);
                setZipcodeError(null);
            }
        }, 500);

        return () => clearTimeout(delayDebounceFn);
    }, [values.zipcode, values.iso2Country]);

    useEffect(() => {
        if (show) {
            if (countries.length > 0) {
                setValues((prev) => ({ ...prev, iso2Country: countries[0].iso2Country }));
            }
        } else {
            resetForm();
            setLocationInfo(null);
            setZipcodeError(null);
        }
    }, [show, countries, resetForm, setValues]);

    const onSubmit = async () => {
        try {
            setLoading(true);

            // Double check validation before submit
            const isValid = await validateZipcode(values.zipcode, values.iso2Country);
            if (!isValid) {
                setLoading(false);
                return;
            }

            const payload: CreateZipcodeDispatchZoneData = {
                iso2Country: values.iso2Country.toUpperCase(),
                zipcode: values.zipcode.trim(),
                status: 1,
            };

            const response = await adminZipcodeDispatchZoneService.createZipcodeDispatchZone(payload);

            if (response.statusCode === 200) {
                onHide();
                onSuccess();
                await Swal.fire({
                    icon: "success",
                    title: "¡Éxito!",
                    text: "La zona de despacho por código postal ha sido registrada correctamente.",
                    confirmButtonText: "Aceptar",
                    confirmButtonColor: "var(--color-primary)",
                });
            } else {
                errorService.showErrorAlert(response, "Error al Crear Zona de Despacho");
            }
        } catch (error: any) {
            console.error("Error in CreateZipcodeDispatchZoneModal:", error);
            errorService.showErrorAlert(error, "Error de Conexión");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            show={show}
            onHide={onHide}
            centered
            size="lg"
            className={styles.createZipcodeModal}
            contentClassName={styles.scaleIn}
            animation={false}
        >
            <Modal.Header closeButton>
                <Modal.Title as="h3" className={modalStyles.modalTitle}>
                    Registrar Nueva Zona de Despacho
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <p className={styles.modalSubtitle}>
                    Completa la información básica para registrar una zona de despacho habilitada por código postal.
                </p>
                <Form onSubmit={handleSubmit(onSubmit)} id="create-zipcode-form" noValidate>
                    <div className={styles.formGrid}>
                        <Form.Group>
                            <Form.Label>País</Form.Label>
                            <div className={styles.inputWrapper}>
                                <FontAwesomeIcon icon={faGlobe} className={styles.inputIcon} />
                                <Form.Select
                                    name="iso2Country"
                                    value={values.iso2Country}
                                    onChange={handleChange}
                                    className={`${styles.createZipcodeModalInput} ${errors.iso2Country ? styles.inputError : ""}`}
                                    disabled={loading || loadingCountries}
                                >
                                    {countries.map((c) => (
                                        <option key={c.iso2Country} value={c.iso2Country}>
                                            {c.name}
                                        </option>
                                    ))}
                                </Form.Select>
                            </div>
                            {errors.iso2Country && <div className={styles.errorText}>{errors.iso2Country}</div>}
                        </Form.Group>

                        <Form.Group>
                            <Form.Label>Código Postal</Form.Label>
                            <div className={styles.inputWrapper}>
                                <FontAwesomeIcon icon={faMapPin} className={styles.inputIcon} />
                                <Form.Control
                                    type="text"
                                    name="zipcode"
                                    placeholder="Código Postal (ej. 1010)"
                                    value={values.zipcode}
                                    onChange={handleChange}
                                    className={`${styles.createZipcodeModalInput} ${errors.zipcode || zipcodeError ? styles.inputError : ""}`}
                                    disabled={loading}
                                    maxLength={20}
                                />
                            </div>
                            {errors.zipcode && <div className={styles.errorText}>{errors.zipcode}</div>}
                            {zipcodeError && <div className={styles.errorText}>{zipcodeError}</div>}
                            {validatingZipcode && (
                                <div style={{ fontSize: "12px", color: "#6b7280", marginTop: "4px" }}>
                                    <Spinner animation="border" size="sm" className="me-1" /> Validando código postal...
                                </div>
                            )}
                        </Form.Group>
                    </div>

                    <div className={styles.formGrid}>
                        <Form.Group>
                            <Form.Label>Ciudad</Form.Label>
                            <Form.Control
                                type="text"
                                value={locationInfo?.city || ""}
                                readOnly
                                placeholder="Se autocompletará con el código postal"
                                className={styles.createZipcodeModalInput}
                                style={{ backgroundColor: "#f8fafc", cursor: "not-allowed" }}
                            />
                        </Form.Group>

                        <Form.Group>
                            <Form.Label>Estado</Form.Label>
                            <Form.Control
                                type="text"
                                value={locationInfo?.state || ""}
                                readOnly
                                placeholder="Se autocompletará con el código postal"
                                className={styles.createZipcodeModalInput}
                                style={{ backgroundColor: "#f8fafc", cursor: "not-allowed" }}
                            />
                        </Form.Group>
                    </div>
                </Form>
            </Modal.Body>

            <Modal.Footer className={styles.modalFooter}>
                <Button
                    variant="secondary"
                    onClick={onHide}
                    className={styles.secondaryBtn}
                    disabled={loading}
                >
                    Cancelar
                </Button>
                <Button
                    variant="primary"
                    type="submit"
                    form="create-zipcode-form"
                    className={modalStyles.closeBtn}
                    disabled={loading}
                >
                    {loading ? (
                        <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Registrando...
                        </>
                    ) : (
                        <>
                            <FontAwesomeIcon icon={faSave} className="me-2" /> Registrar
                        </>
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default CreateZipcodeDispatchZoneModal;
